<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  importId: { type: [String, Number], required: true },
});

const emit = defineEmits(['complete', 'close']);

const importStatus = ref('pending');
const importProcessed = ref(0);
const importTotal = ref(0);
const isMinimized = ref(false);
let pollTimer = null;

const importProgress = computed(() => {
  if (!importTotal.value) return 0;
  return Math.min(
    100,
    Math.round((importProcessed.value / importTotal.value) * 100)
  );
});

const isDone = computed(
  () => importStatus.value === 'completed' || importStatus.value === 'failed'
);

const statusLabel = computed(() => {
  if (importStatus.value === 'pending') return 'Aguardando processamento...';
  if (importStatus.value === 'processing') return 'Processando contatos...';
  if (importStatus.value === 'completed') return 'Importação concluída!';
  if (importStatus.value === 'failed') return 'Erro na importação';
  return '';
});

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const poll = async () => {
  try {
    const res = await ContactAPI.getImportStatus(props.importId);
    const data = res.data;
    importStatus.value = data.status;
    importProcessed.value = data.processed_records || 0;
    importTotal.value = data.total_records || 0;
    if (isDone.value) {
      stopPolling();
      if (importStatus.value === 'completed') emit('complete');
    }
  } catch {
    // keep polling
  }
};

const handleClose = () => {
  stopPolling();
  emit('close');
};

onMounted(() => {
  poll();
  pollTimer = setInterval(poll, 2000);
});

onUnmounted(stopPolling);
</script>

<template>
  <div
    class="fixed bottom-4 ltr:right-4 rtl:left-4 z-50 w-80 rounded-xl shadow-lg border border-n-weak bg-n-background overflow-hidden"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
    >
      <div class="flex items-center gap-2">
        <span
          v-if="importStatus === 'pending'"
          class="i-lucide-clock text-n-slate-8 size-4 shrink-0"
        />
        <span
          v-else-if="importStatus === 'processing'"
          class="i-lucide-loader-circle animate-spin text-n-brand size-4 shrink-0"
        />
        <span
          v-else-if="importStatus === 'completed'"
          class="i-lucide-circle-check text-emerald-500 size-4 shrink-0"
        />
        <span
          v-else-if="importStatus === 'failed'"
          class="i-lucide-circle-x text-red-500 size-4 shrink-0"
        />
        <span class="text-sm font-medium text-n-slate-12">{{
          statusLabel
        }}</span>
      </div>
      <div class="flex items-center gap-1">
        <button
          class="text-n-slate-10 hover:text-n-slate-12 p-1 rounded"
          @click="isMinimized = !isMinimized"
        >
          <span
            :class="
              isMinimized ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
            class="size-4 block"
          />
        </button>
        <button
          v-if="isDone"
          class="text-n-slate-10 hover:text-n-slate-12 p-1 rounded"
          @click="handleClose"
        >
          <span class="i-lucide-x size-4 block" />
        </button>
      </div>
    </div>

    <!-- Body (collapsible) -->
    <div v-if="!isMinimized" class="px-4 py-3 flex flex-col gap-3">
      <!-- Progress bar -->
      <div class="w-full bg-n-alpha-2 rounded-full h-1.5 overflow-hidden">
        <div
          class="h-1.5 rounded-full transition-all duration-300"
          :class="importStatus === 'failed' ? 'bg-red-500' : 'bg-n-brand'"
          :style="{
            width: `${importStatus === 'pending' ? 0 : importProgress}%`,
          }"
        />
      </div>

      <!-- Counts -->
      <div class="flex justify-between text-xs text-n-slate-11">
        <span v-if="importTotal > 0">
          {{ importProcessed.toLocaleString('pt-BR') }} /
          {{ importTotal.toLocaleString('pt-BR') }} linhas
        </span>
        <span v-else>Calculando total...</span>
        <span class="font-medium text-n-slate-12">{{ importProgress }}%</span>
      </div>

      <!-- Keep page open hint -->
      <div
        v-if="!isDone"
        class="rounded-lg bg-n-alpha-1 border border-n-weak p-2 text-xs text-n-slate-11 flex items-start gap-1.5"
      >
        <span class="i-lucide-info size-3.5 shrink-0 mt-0.5" />
        Mantenha esta página aberta enquanto a importação estiver em andamento.
      </div>

      <!-- Completion summary -->
      <div
        v-if="importStatus === 'completed'"
        class="rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 p-2 text-xs"
      >
        <p class="font-medium text-emerald-700 dark:text-emerald-300">
          {{ importProcessed.toLocaleString('pt-BR') }} contatos importados
          <template v-if="importTotal - importProcessed > 0">
            ·
            {{ (importTotal - importProcessed).toLocaleString('pt-BR') }}
            falharam
          </template>
        </p>
      </div>

      <!-- Failure message -->
      <div
        v-if="importStatus === 'failed'"
        class="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-2 text-xs text-red-700 dark:text-red-300"
      >
        Falha no processamento. Verifique o CSV e tente novamente.
      </div>
    </div>
  </div>
</template>
